/* ==========================================================================
   Hero — hero-firstlight.js
   Minimal, dependency-free slide switcher.
   Options (data-*): autoplay, speed, pause-on-hover, swipe, effect, duration.
   Effects: 'fade' | 'slide' | 'none'. Both keep non-active slides [hidden]
   at rest, so lazy images are not fetched until a slide is first shown.
   ========================================================================== */

(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('hero-firstlight')) {
    return;
  }

  customElements.define(
    'hero-firstlight',
    class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(this.querySelectorAll('.hero-fl__slide'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hero-dot]'));
        this.index = 0;
        this.timer = 0;
        this.transitionTimer = 0;
        this.touchX = null;
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.effect = this.reduced ? 'none' : this.dataset.effect || 'fade';
        this.duration = Math.max(0, parseInt(this.dataset.duration, 10) || 600);
        this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;
        this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
        this.swipe = this.dataset.swipe !== 'false';

        var self = this;

        this.onPrev = function () { self.show(self.index - 1); };
        this.onNext = function () { self.show(self.index + 1); };
        this.onKey = function (e) {
          if (e.key === 'ArrowLeft') self.show(self.index - 1);
          if (e.key === 'ArrowRight') self.show(self.index + 1);
        };
        this.onEnter = function () { self.pause(); };
        this.onLeave = function () { self.resume(); };
        this.onTouchStart = function (e) {
          self.touchX = e.changedTouches[0].clientX;
        };
        this.onTouchEnd = function (e) {
          if (self.touchX === null) return;
          var dx = e.changedTouches[0].clientX - self.touchX;
          self.touchX = null;
          if (Math.abs(dx) > 40) self.show(dx < 0 ? self.index + 1 : self.index - 1);
        };

        var prev = this.querySelector('[data-hero-prev]');
        var next = this.querySelector('[data-hero-next]');
        if (prev) prev.addEventListener('click', this.onPrev);
        if (next) next.addEventListener('click', this.onNext);
        this.dots.forEach(function (d, i) {
          d.addEventListener('click', function () { self.show(i); });
        });

        if (this.slides.length > 1) {
          this.addEventListener('keydown', this.onKey);

          if (this.pauseOnHover) {
            this.addEventListener('mouseenter', this.onEnter);
            this.addEventListener('mouseleave', this.onLeave);
            this.addEventListener('focusin', this.onEnter);
            this.addEventListener('focusout', this.onLeave);
          }

          if (this.swipe) {
            this.addEventListener('touchstart', this.onTouchStart, { passive: true });
            this.addEventListener('touchend', this.onTouchEnd, { passive: true });
          }
        }

        if (this.slides.length) this.show(0, true);
        this.resume();
      }

      disconnectedCallback() {
        this.pause();
        clearTimeout(this.transitionTimer);
      }

      setCurrent(index) {
        this.dots.forEach(function (d, k) {
          var active = k === index;
          d.classList.toggle('is-active', active);
          if (active) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      show(i, immediate) {
        var n = this.slides.length;
        if (!n) return;

        var next = ((i % n) + n) % n;
        var prev = this.index;
        var s = this.slides;
        this.index = next;
        this.setCurrent(next);

        // Instant path: no effect, or reduced motion, or initial paint.
        if (this.effect === 'none' || immediate) {
          s.forEach(function (el, k) {
            el.classList.remove('is-out');
            el.classList.toggle('is-active', k === next);
            if (k === next) el.removeAttribute('hidden');
            else el.setAttribute('hidden', '');
          });
          return;
        }

        // Cross-fade / slide: keep both slides visible during the transition.
        s.forEach(function (el, k) {
          if (k !== next) el.classList.remove('is-active');
        });

        s[next].classList.remove('is-out');
        s[next].removeAttribute('hidden');
        void s[next].offsetWidth; // force reflow so the transition runs
        s[next].classList.add('is-active');

        if (prev !== next) {
          s[prev].removeAttribute('hidden');
          s[prev].classList.add('is-out');
        }

        var self = this;
        clearTimeout(this.transitionTimer);
        this.transitionTimer = setTimeout(function () {
          s.forEach(function (el, k) {
            el.classList.remove('is-out');
            el.classList.toggle('is-active', k === next);
            if (k !== next) el.setAttribute('hidden', '');
          });
        }, this.duration);
      }

      pause() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = 0;
        }
      }

      resume() {
        if (!this.autoplay || this.timer || this.slides.length < 2) return;
        var self = this;
        var speed = Math.max(3, parseInt(this.dataset.speed, 10) || 7) * 1000;
        this.timer = setInterval(function () { self.show(self.index + 1); }, speed);
      }
    }
  );
})();
